import { useRxCollectionEpocusEncounter } from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { indicationOptions } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import type { ExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider/provider";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import * as Sentry from "@sentry/react";
import { useMemo, useState } from "react";
import { ChipRow, chipStyle } from "../epocus-chip";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../epocus-palette";
import { PatientSearchField } from "../patient-link/patient-search-field";
import { usePatientLinkingEnabled } from "../patient-link/use-patient-linking-enabled";
import type { PatientSearchResult } from "../patient-link/use-patient-search";
import { useActiveProgramId } from "../use-active-program";
import {
  splitProcedureOptions,
  useEnabledExamTypeOptions,
} from "../use-enabled-exam-types";
import { useProgramDirectory } from "../use-program-directory";
import {
  DEFAULT_REQUEST_PRIORITY,
  REQUEST_PRIORITY_OPTIONS,
} from "./scan-request-display";
import { createScanRequest } from "./scan-request-operations";

const FieldLabel: FCwC = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(12),
      fontWeight: 700,
      letterSpacing: "0.08em",
      color: EPOCUS_TEXT_SECONDARY,
      textTransform: "uppercase",
      marginBottom: 6,
    }}
  >
    {children}
  </Typography>
);

const POOL_VALUE = "__pool__";

export const NewScanRequestSheet: FC<{
  open: boolean;
  onClose: () => void;
  onCreated?: (encounterId: string) => void;
}> = ({ open, onClose, onCreated }) => {
  const user = useUser();
  const programId = useActiveProgramId();
  const encounterCollection = useRxCollectionEpocusEncounter();
  const examTypeOptions = useEnabledExamTypeOptions();
  const { standard: standardExamOptions, procedures: procedureExamOptions } =
    splitProcedureOptions(examTypeOptions);
  const renderExamChip = (option: (typeof examTypeOptions)[number]) => (
    <Chip
      key={option.type}
      label={option.label}
      variant="outlined"
      clickable
      onClick={() => setExamType(option.type)}
      aria-pressed={examType === option.type}
      style={chipStyle(examType === option.type)}
    />
  );
  const { members } = useProgramDirectory(programId);

  const patientLinkingEnabled = usePatientLinkingEnabled();
  const [linkToRecord, setLinkToRecord] = useState(true);
  const linkPatient = patientLinkingEnabled && linkToRecord;
  const [selectedPatient, setSelectedPatient] =
    useState<PatientSearchResult | null>(null);
  const [patientName, setPatientName] = useState("");
  const [patientDob, setPatientDob] = useState("");
  const [patientMrn, setPatientMrn] = useState("");
  const [examType, setExamType] = useState<ExamType | null>(null);
  const [indication, setIndication] = useState("");
  const [priority, setPriority] = useState(DEFAULT_REQUEST_PRIORITY);
  const [notes, setNotes] = useState("");
  const [performerId, setPerformerId] = useState(POOL_VALUE);
  const [saving, setSaving] = useState(false);

  const indicationChips = useMemo(
    () => (examType ? indicationOptions(examType) : []),
    [examType],
  );

  const hasPatient = linkPatient
    ? selectedPatient !== null
    : patientName.trim() !== "";
  const canSubmit = hasPatient && examType !== null && !saving;

  const reset = () => {
    setLinkToRecord(true);
    setSelectedPatient(null);
    setPatientName("");
    setPatientDob("");
    setPatientMrn("");
    setExamType(null);
    setIndication("");
    setPriority(DEFAULT_REQUEST_PRIORITY);
    setNotes("");
    setPerformerId(POOL_VALUE);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!canSubmit || !encounterCollection || examType === null) return;

    setSaving(true);
    try {
      const encounterId = await createScanRequest({
        encounterCollection,
        userId: user.id,
        programId,
        draft: {
          elephantPatientId: linkPatient
            ? (selectedPatient?.patientId ?? null)
            : null,
          patientName: linkPatient
            ? (selectedPatient?.displayName ?? null)
            : patientName.trim(),
          patientDob: linkPatient ? null : patientDob.trim() || null,
          patientMrn: linkPatient ? null : patientMrn.trim() || null,
          requestedExamType: examType,
          indication: indication.trim() || null,
          requestPriority: priority,
          requestNotes: notes.trim() || null,
          performerId: performerId === POOL_VALUE ? null : performerId,
        },
      });
      toast.success("Scan request raised");
      onCreated?.(encounterId);
      close();
    } catch (error) {
      toast.error("Couldn't raise the scan request. Please try again.");
      Sentry.captureException(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography
          style={{
            fontSize: rem(18),
            fontWeight: 700,
            color: EPOCUS_TEXT_PRIMARY,
          }}
        >
          New scan request
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ gap: 18 }}>
          <Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <FieldLabel>Patient</FieldLabel>
              {patientLinkingEnabled && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={linkToRecord}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setLinkToRecord(e.target.checked)
                      }
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Typography
                      style={{
                        fontSize: rem(13),
                        color: EPOCUS_TEXT_SECONDARY,
                      }}
                    >
                      Link to a record
                    </Typography>
                  }
                  style={{ marginRight: 0 }}
                />
              )}
            </Box>
            {linkPatient ? (
              <PatientSearchField
                selectedPatient={selectedPatient}
                onSelect={setSelectedPatient}
              />
            ) : (
              <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Patient name"
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  inputProps={{ "aria-label": "Patient name" }}
                />
                <Box display="flex" style={{ gap: 10 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="date"
                    label="Date of birth"
                    value={patientDob}
                    onChange={(e) => setPatientDob(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ "aria-label": "Date of birth" }}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="MRN"
                    value={patientMrn}
                    onChange={(e) => setPatientMrn(e.target.value)}
                    inputProps={{ "aria-label": "MRN" }}
                  />
                </Box>
              </Box>
            )}
          </Box>

          <Box>
            <FieldLabel>Exam type</FieldLabel>
            <ChipRow>{standardExamOptions.map(renderExamChip)}</ChipRow>
            {procedureExamOptions.length > 0 && (
              <Box style={{ marginTop: 12 }}>
                <FieldLabel>Procedures</FieldLabel>
                <ChipRow>{procedureExamOptions.map(renderExamChip)}</ChipRow>
              </Box>
            )}
          </Box>

          <Box>
            <FieldLabel>Indication</FieldLabel>
            {indicationChips.length > 0 && (
              <ChipRow style={{ marginBottom: 10 }}>
                {indicationChips.map((option) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    variant="outlined"
                    clickable
                    onClick={() => setIndication(option.label)}
                    aria-pressed={indication === option.label}
                    style={chipStyle(indication === option.label)}
                  />
                ))}
              </ChipRow>
            )}
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Why is this scan needed?"
              value={indication}
              onChange={(e) => setIndication(e.target.value)}
              inputProps={{ "aria-label": "Indication" }}
            />
          </Box>

          <Box>
            <FieldLabel>Priority</FieldLabel>
            <ChipRow>
              {REQUEST_PRIORITY_OPTIONS.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  variant="outlined"
                  clickable
                  onClick={() => setPriority(option.value)}
                  aria-pressed={priority === option.value}
                  style={chipStyle(priority === option.value)}
                />
              ))}
            </ChipRow>
          </Box>

          <Box>
            <FieldLabel>Performer</FieldLabel>
            <Select
              fullWidth
              variant="outlined"
              margin="dense"
              value={performerId}
              onChange={(e) => setPerformerId(String(e.target.value))}
              inputProps={{ "aria-label": "Performer" }}
            >
              <MenuItem value={POOL_VALUE}>
                Anyone in the program (pool)
              </MenuItem>
              {members.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <FieldLabel>Notes</FieldLabel>
            <TextField
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              size="small"
              placeholder="Anything the performer should know"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              inputProps={{ "aria-label": "Notes" }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} style={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          onClick={submit}
          style={{ textTransform: "none", fontWeight: 700 }}
        >
          Raise request
        </Button>
      </DialogActions>
    </Dialog>
  );
};
